import React, { Component } from 'react';
import { bool, string, object } from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import get from 'lodash.get';
import classnames from 'classnames';

import { localStorage } from 'dibs-browser-storage';

import Dropdown, { alignConstants } from '../Dropdown/Dropdown';
import DropdownBody from '../Dropdown/DropdownBody';
import DropdownLoadingContent from '../Dropdown/DropdownLoadingContent';
import NotificationsIcon from './NotificationsIcon';
import ActivityFeedLazy from './ActivityFeedLazy';
import ConversationsLazy from './ConversationsLazy';
import { setPromoCodeNotificationShown } from '../../utils/promoCode.es';
import { getNotificationName, trackNotification } from './notificationTracking';

//imports for shareActivityAlert
import { trackEvent } from 'dibs-tracking';
import { shareActivitiesDisplay } from './trackingHelpers';
import ShareActivityAlert from './ShareActivityAlert';
import { shouldDisableNotifications } from '../../utils/notificationsHelper';
import {
    getUserSessionCountryCode,
    hasUserSessionCountryCode,
} from 'dibs-regional-info/exports/regionalInfoHelpers';

import styles from './Notifications-style.scss';

const BATCH_SIZE = 5;
const AUTOMATED_OFFER_VIEW_COUNT_DROPDOWN_TRIGGER_TRESHOLD = 2;
const AUTOMATED_OFFER_COUNT_STORAGE_KEY = 'AUTOMATED_OFFER_COUNT_STORAGE_KEY';
const SHARE_ACTIVITY_AUTO_COUNT_STORAGE_KEY = 'SHARE_ACTIVITY_AUTO_COUNT_STORAGE_KEY';
const DATE_OF_VIEWS_STORAGE_KEY = 'DATE_OF_VIEWS_STORAGE_KEY';
export const LOCAL_STORAGE_KEY_NAME = 'NAV_NOTIFICATIONS_DROPDOWN_LAST_OPEN_TIME';

const resetState = () => ({
    isOpen: false,
    fetchingNotifications: true,
    first: BATCH_SIZE,
});
const stopFetching = (isMore = false) => {
    const defaultReturn = { fetchingNotifications: false };
    return isMore ? Object.assign({}, defaultReturn) : defaultReturn;
};
const startFetching = () => ({ fetchingNotifications: true });
const openDropdown = () => ({ isOpen: true });

const isNewNotification = eventDate =>
    new Date(eventDate).getTime() > Number(localStorage.getItem(LOCAL_STORAGE_KEY_NAME)) || 0;
const trackNotificationAlerts = notifications => {
    notifications.forEach(notification => {
        const { eventDate, type } = notification;
        if (type === 'TRADE_REWARD') {
            return;
        }

        if (isNewNotification(eventDate)) {
            trackNotification(getNotificationName(type), 'alert displayed');
        }
    });
};

function hasShowActivityAlertExpired() {
    let endDate = localStorage.getItem(DATE_OF_VIEWS_STORAGE_KEY);

    if (endDate) {
        endDate = new Date(parseInt(endDate, 10));
    } else {
        endDate = new Date();
        const sixWeeks = 42;
        endDate.setDate(endDate.getDate() + sixWeeks);
        localStorage.setItem(DATE_OF_VIEWS_STORAGE_KEY, endDate.getTime());
    }
    return endDate < new Date();
}

export class NotificationsComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            ...resetState(),
            showShareActivity: false,
            triggerDropdown: false,
            activePromotion: null,
        };

        this.dropdownBodyRef = React.createRef();
        this.lastReadTime = 0;

        this.loadNotifications = this.loadNotifications.bind(this);
        this.onMouseEnterIcon = this.onMouseEnterIcon.bind(this);
        this.handleDropdownBlur = this.handleDropdownBlur.bind(this);
        this.handleDropdownToggle = this.handleDropdownToggle.bind(this);
        this.onActivePromotionFetched = this.onActivePromotionFetched.bind(this);
        this.onActivityFeedFetched = this.onActivityFeedFetched.bind(this);
        this.showShareActivityAlert = this.showShareActivityAlert.bind(this);
    }

    componentDidMount() {
        this.lastReadTime = Number(localStorage.getItem(LOCAL_STORAGE_KEY_NAME)) || 0;
    }

    showShareActivityAlert() {
        const props = this.props;
        const { viewer, user } = props;
        const isBuyerActivityDropdownDisabled = false;
        const timesShown = localStorage.getItem(SHARE_ACTIVITY_AUTO_COUNT_STORAGE_KEY) || 0;
        const isExpired = hasShowActivityAlertExpired();
        const countryCode = getUserSessionCountryCode() || get(viewer, 'regInfo[0].countryCode');
        const isUS = countryCode === 'US';
        const shareActivities = get(user, 'preferences.shareActivities') || false;

        if (
            !isUS &&
            !shareActivities &&
            !isExpired &&
            !isBuyerActivityDropdownDisabled &&
            !shouldDisableNotifications()
        ) {
            const triggerDropdown = timesShown <= 3;
            this.setState(
                {
                    showShareActivity: true,
                    triggerDropdown,
                },
                () => {
                    if (triggerDropdown) {
                        const trigger = 'auto-displayed';
                        trackEvent(shareActivitiesDisplay({ trigger, timesShown }));
                        localStorage.setItem(SHARE_ACTIVITY_AUTO_COUNT_STORAGE_KEY, timesShown + 1);
                        this.loadNotifications();
                    }
                }
            );
        }
    }

    showAutomatedOfferAlert(activityFeed) {
        const { viewer } = this.props;

        const isAutomatedOfferNotificationDisabled = get(
            viewer,
            'isAutomatedOfferNotificationDisabled'
        );

        if (shouldDisableNotifications() || isAutomatedOfferNotificationDisabled) {
            return;
        }

        const savedAutomatedOfferViewCounts =
            localStorage.getItem(AUTOMATED_OFFER_COUNT_STORAGE_KEY) || {};

        const automatedOfferViewCounts = activityFeed.reduce((viewCounts, currentNotification) => {
            if (currentNotification.node.type === 'AUTOMATED_OFFER') {
                const notificationId = currentNotification.node.serviceId;
                return {
                    ...viewCounts,
                    [notificationId]: (savedAutomatedOfferViewCounts[notificationId] || 0) + 1,
                };
            }
            return viewCounts;
        }, {});

        const smallestViewCount = Math.min(...Object.values(automatedOfferViewCounts));
        const triggerDropdown =
            smallestViewCount <= AUTOMATED_OFFER_VIEW_COUNT_DROPDOWN_TRIGGER_TRESHOLD;

        this.setState({ triggerDropdown }, () => {
            if (triggerDropdown) {
                this.loadNotifications();
                localStorage.setItem(AUTOMATED_OFFER_COUNT_STORAGE_KEY, automatedOfferViewCounts);
            }
        });
    }

    onActivityFeedFetched(activityFeed) {
        const feed = activityFeed?.edges;
        if (Array.isArray(feed)) {
            trackNotificationAlerts(feed.map(edge => edge.node));
            this.showAutomatedOfferAlert(feed);
        }
    }

    onActivePromotionFetched(activePromotion) {
        this.setState({ activePromotion });
    }

    loadNotifications() {
        const { hasRootPendingRefetch, relay, userId } = this.props;
        const fetchNotificationVariables = {
            fetchNotifications: true,
            userId,
            hasUserId: !!userId,
            hasSessionCountryCode: hasUserSessionCountryCode(),
        };

        if (hasRootPendingRefetch) {
            return;
        }

        const refetchVariables = fragmentVariables =>
            Object.assign({}, fragmentVariables, fetchNotificationVariables);
        const refetchCallback = () => {
            localStorage.setItem(LOCAL_STORAGE_KEY_NAME, Date.now());
            if (this.state.activePromotion) {
                setPromoCodeNotificationShown(this.state.activePromotion);
            }
            this.setState(stopFetching);
        };

        this.setState(startFetching, () =>
            relay.refetch(refetchVariables, null, refetchCallback, { force: true })
        );
    }

    onMouseEnterIcon(e) {
        e.stopPropagation();
        if (!this.state.isOpen) {
            this.loadNotifications();
            this.setState(openDropdown);
        }
    }

    handleDropdownToggle() {
        if (!this.state.isOpen) {
            this.loadNotifications();
            this.setState({ isOpen: true, triggerDropdown: true });
        } else {
            this.setState({ isOpen: false, triggerDropdown: false });
        }
    }
    handleDropdownBlur(e) {
        if (this.dropdownBodyRef.current && !this.dropdownBodyRef.current.contains(e.relatedTarget))
            this.setState({ isOpen: false, triggerDropdown: false });
    }

    render() {
        const {
            hasUserId,
            isClient,
            user,
            viewer,
            initialViewer,
            isVipCuratedFolderTooltipVisible,
        } = this.props;
        const { fetchingNotifications, showShareActivity, triggerDropdown } = this.state;

        if (!isClient || !hasUserId || !user) {
            return null;
        }

        const conversations = get(this.props, 'viewer.conversations') || null;

        return (
            <Dropdown
                hasDropdownHeader
                align={alignConstants.ALIGN_RIGHT}
                forceOpen={triggerDropdown}
                onHide={() => {
                    this.setState(resetState);
                }}
                className={classnames({
                    [styles.noHover]: isVipCuratedFolderTooltipVisible,
                })}
            >
                <NotificationsIcon
                    viewer={initialViewer}
                    user={user}
                    isOpen={this.state.isOpen}
                    onActivePromotionFetched={this.onActivePromotionFetched}
                    onMouseEnter={this.onMouseEnterIcon}
                    onClick={this.handleDropdownToggle}
                />
                <DropdownBody ref={this.dropdownBodyRef} onBlur={this.handleDropdownBlur}>
                    <React.Fragment>
                        {showShareActivity && (
                            <ShareActivityAlert
                                user={user}
                                viewer={viewer}
                                autoDisplayed={triggerDropdown}
                            />
                        )}
                        <React.Suspense fallback={<DropdownLoadingContent />}>
                            <ActivityFeedLazy
                                viewer={viewer}
                                user={user}
                                lastReadTime={this.lastReadTime}
                                onActivityFeedFetched={this.onActivityFeedFetched}
                            />
                            <ConversationsLazy
                                viewer={initialViewer}
                                conversations={conversations}
                                isLoading={fetchingNotifications}
                            />
                        </React.Suspense>
                    </React.Fragment>
                </DropdownBody>
            </Dropdown>
        );
    }
}

NotificationsComponent.propTypes = {
    hasUserId: bool,
    hasRootPendingRefetch: bool,
    isClient: bool,
    relay: object,
    userId: string,
    initialViewer: object,
    viewer: object,
    user: object,
    isVipCuratedFolderTooltipVisible: bool,
};

const mapStateToProps = ({ header }) => {
    const isVipCuratedFolderTooltipVisible = header?.isVipCuratedFolderTooltipVisible;
    return {
        isVipCuratedFolderTooltipVisible,
    };
};

const Notifications = createRefetchContainer(
    connect(mapStateToProps)(NotificationsComponent),
    {
        initialViewer: graphql`
            fragment Notifications_initialViewer on Viewer {
                ...Conversations_viewer @include(if: $isClient) @defer
                ...NotificationsIcon_viewer @include(if: $isClient) @defer
            }
        `,
        viewer: graphql`
            fragment Notifications_viewer on Viewer
            @argumentDefinitions(
                first: { type: "Int", defaultValue: 5 }
                fetchNotifications: { type: "Boolean!", defaultValue: false }
                hasSessionCountryCode: { type: "Boolean!", defaultValue: false }
            ) {
                regInfo: regionalInfo @skip(if: $hasSessionCountryCode) {
                    countryCode
                }
                isAutomatedOfferNotificationDisabled: isEnabled(
                    key: "disableAutomatedOfferBellNotification"
                )
                conversations(filter: "showUnreadOnly", first: $first, notifications: true)
                @include(if: $fetchNotifications) {
                    ...Conversations_conversations
                }
                ...ActivityFeed_viewer @include(if: $isClient) @defer
            }
        `,
        user: graphql`
            fragment Notifications_user on User {
                preferences {
                    shareActivities
                }
                ... @include(if: $isClient) {
                    ...ShareActivityAlert_user @defer
                    ...ActivityFeed_user @defer
                    ...NotificationsIcon_user @defer
                }
            }
        `,
    },
    graphql`
        query NotificationsRefetchQuery(
            $first: Int
            $fetchNotifications: Boolean!
            $hasSessionCountryCode: Boolean!
            $isClient: Boolean = true
        ) {
            viewer {
                ...Notifications_viewer
                @arguments(
                    first: $first
                    fetchNotifications: $fetchNotifications
                    hasSessionCountryCode: $hasSessionCountryCode
                )
            }
        }
    `
);

export default Notifications;
